import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { VFStatCard } from '@/components/vf/StatCard';
import { formatDateTime } from '@/i18n/locale';
import { usePortalKpis, PORTAL_KPI_DAYS } from '@/hooks/usePortalKpis';
import { meetingTime } from '@/lib/portalMeetings';
import { usePortalAwaitingMeetings } from '@/hooks/usePortalAwaitingMeetings';
import CallsTable from '@/components/backoffice/CallsTable';
import { READY, type SectionLoad } from './portalFigures';

/**
 * The client portal's home (W5, B3-ClientDashboard, DES-11, DES-20).
 *
 * In order of importance:
 *   1. « RDV à valider »: the client's task, the meetings it still has to
 *      validate, each with « Valider », which opens Mes rendez-vous on its
 *      « À valider » tab (REV-W5-9: no more scroll to a gone #validation).
 *      Same list and count as that tab and the menu badge (REV-W5-6,
 *      usePortalAwaitingMeetings);
 *   2. four figures over the last 30 days with their trend against the 30
 *      days before: RDV obtenus, Appels, Taux de contact (connected / calls),
 *      Conversion (meetings / connected). Server aggregates, usePortalKpis;
 *   3. « Derniers appels », the five latest; while the page's calls load or
 *      when they failed (`callsLoad`, REV-W5-1), a skeleton or the error with
 *      « Réessayer », not « Aucun appel ».
 * Removed (audit: ten blocks of equal weight, colours that encoded nothing):
 * quick actions, notification centre (the bell stays in the header),
 * prospects remaining, calls today, the 30-day chart (Performance tab),
 * weekly report, internal notes, upcoming meetings, help card (Aide tab).
 * No gradients, no hover shadows, no staggered reveals.
 *
 * `customerId` narrows the figures to one customer (agency preview); a
 * viewer leaves it empty, the server scopes to its customers.
 */

interface ClientLite {
  id?: string;
  email?: string | null;
  contact_name?: string | null;
  company_name?: string | null;
}

interface CallRow {
  id?: string;
  prospect_name?: string | null;
  prospect_company?: string | null;
  call_date?: string | Date | null;
  duration_seconds?: number | null;
  outcome?: string | null;
  recording_url?: string | null;
  meeting_date?: string | Date | null;
}

interface HomeSectionProps {
  client?: ClientLite | null;
  calls: CallRow[];
  onTabChange: (id: string) => void;
  onPlayRecording?: (url: string, title?: string) => void;
  customerId?: string | null;
  /** Loading / failed state of `calls` (REV-W5-1); ready when absent. */
  callsLoad?: SectionLoad;
  /**
   * QA-W5-4: the signed-in viewer's name (its portal profile). When given,
   * the greeting uses it only, never the organisation's contact of `client`;
   * absent (the dialer preview), the client's contact or company.
   */
  viewerName?: string | null;
}

const LIST_MAX = 5;

export default function HomeSection({ client, calls, onTabChange, onPlayRecording, customerId = null, callsLoad = READY, viewerName }: HomeSectionProps) {
  const { t } = useTranslation(['admin', 'common']);
  // A viewer is known by its email; the agency preview by the customer it shows.
  const enabled = !!client?.email || !!customerId;

  const { query: meetingsQuery, rows: toValidate } = usePortalAwaitingMeetings({ scope: client?.email, customerId, enabled });

  const kpis = usePortalKpis(customerId, enabled);
  const tileState = kpis.isLoading ? 'loading' : kpis.isError ? 'error' : 'ready';
  const k = kpis.data;

  const name = viewerName !== undefined
    ? (viewerName ?? '').trim().split(/\s+/)[0] ?? ''
    : client?.contact_name?.split(' ')[0] || client?.company_name || '';
  const lastCalls = (calls || []).slice(0, LIST_MAX);

  return (
    <div className="space-y-8">
      {name && (
        <p className="text-sm text-gray-600" data-testid="portal-greeting">
          {t('admin:backoffice.home.greeting', { name })}
        </p>
      )}

      {/* 1. The client's task */}
      <section aria-labelledby="portal-home-validate" className="rounded-lg border border-gray-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
          <h2 id="portal-home-validate" className="text-base font-semibold text-gray-900">
            {t('admin:backoffice.home.meetingsToValidate')}
            {meetingsQuery.isSuccess && toValidate.length > 0 && (
              <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 tabular-nums">{toValidate.length}</span>
            )}
          </h2>
          {meetingsQuery.isSuccess && toValidate.length > 0 && (
            <Button variant="ghost" size="sm" className="min-h-11" onClick={() => onTabChange('meetings')}>
              {t('admin:backoffice.home.viewAllMeetings')}
            </Button>
          )}
        </div>
        {meetingsQuery.isLoading ? (
          <div className="space-y-2 p-4" role="status" aria-label={t('common:actions.loading')}>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-10 animate-pulse rounded bg-gray-100 motion-reduce:animate-none" />
            ))}
          </div>
        ) : meetingsQuery.isError ? (
          <div className="flex flex-wrap items-center justify-between gap-2 p-4" role="alert">
            <p className="text-sm text-gray-700">{t('admin:backoffice.home.toValidateError')}</p>
            <Button variant="outline" size="sm" className="min-h-11" onClick={() => meetingsQuery.refetch()}>
              {t('common:actions.retry')}
            </Button>
          </div>
        ) : toValidate.length === 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-2 p-4">
            <p className="text-sm text-gray-600">{t('admin:backoffice.home.toValidateEmpty')}</p>
            <Button variant="outline" size="sm" className="min-h-11" onClick={() => onTabChange('meetings')}>
              {t('admin:backoffice.home.viewAllMeetings')}
            </Button>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100" data-testid="portal-to-validate">
            {toValidate.slice(0, LIST_MAX).map((m) => {
              const at = meetingTime(m);
              return (
                <li key={m.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">{m.prospect_name || t('admin:backoffice.home.unnamedProspect')}</p>
                    {m.prospect_company && <p className="truncate text-xs text-gray-500">{m.prospect_company}</p>}
                  </div>
                  <p className="text-sm text-gray-700 tabular-nums">{at ? formatDateTime(at) : '—'}</p>
                  <Button size="sm" className="min-h-11" onClick={() => onTabChange('meetings')}>
                    {t('admin:backoffice.home.validate')}
                  </Button>
                </li>
              );
            })}
            {toValidate.length > LIST_MAX && (
              <li className="px-4 py-3 text-sm text-gray-600">
                {t('admin:backoffice.home.moreToValidate', { count: toValidate.length - LIST_MAX })}
              </li>
            )}
          </ul>
        )}
      </section>

      {/* 2. Four figures, real trends */}
      <section aria-labelledby="portal-home-kpis">
        <h2 id="portal-home-kpis" className="mb-3 text-base font-semibold text-gray-900">
          {t('admin:backoffice.home.kpis.title', { days: PORTAL_KPI_DAYS })}
        </h2>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <VFStatCard label={t('admin:backoffice.home.kpis.meetings')} state={tileState} metric={k?.meetings} series={k?.series.meetings} hint={t('admin:backoffice.home.kpis.periodHint', { days: PORTAL_KPI_DAYS })} onClick={() => onTabChange('meetings')} />
          <VFStatCard label={t('admin:backoffice.home.kpis.calls')} state={tileState} metric={k?.calls} series={k?.series.calls} hint={t('admin:backoffice.home.kpis.periodHint', { days: PORTAL_KPI_DAYS })} onClick={() => onTabChange('calls')} />
          <VFStatCard label={t('admin:backoffice.home.kpis.contact')} state={tileState} metric={k?.contact} hint={t('admin:backoffice.home.kpis.contactHint', { days: PORTAL_KPI_DAYS })} onClick={() => onTabChange('performance')} />
          <VFStatCard label={t('admin:backoffice.home.kpis.conversion')} state={tileState} metric={k?.conversion} hint={t('admin:backoffice.home.kpis.conversionHint', { days: PORTAL_KPI_DAYS })} onClick={() => onTabChange('performance')} />
        </div>
        {kpis.isError && (
          <div className="mt-2 flex items-center gap-2">
            <Button variant="ghost" size="sm" className="min-h-11" onClick={() => kpis.refetch()}>{t('common:actions.retry')}</Button>
          </div>
        )}
      </section>

      {/* 3. Last calls */}
      <section aria-labelledby="portal-home-calls" className="rounded-lg border border-gray-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
          <h2 id="portal-home-calls" className="text-base font-semibold text-gray-900">{t('admin:backoffice.home.lastCalls')}</h2>
          {callsLoad.status === 'ready' && lastCalls.length > 0 && (
            <Button variant="ghost" size="sm" className="min-h-11" onClick={() => onTabChange('calls')}>
              {t('admin:backoffice.home.viewAllCalls')}
            </Button>
          )}
        </div>
        {callsLoad.status === 'loading' ? (
          <div className="space-y-2 p-4" role="status" aria-label={t('common:actions.loading')} data-testid="portal-last-calls-loading">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-10 animate-pulse rounded bg-gray-100 motion-reduce:animate-none" />
            ))}
          </div>
        ) : callsLoad.status === 'error' ? (
          <div className="flex flex-wrap items-center justify-between gap-2 p-4" role="alert">
            <div>
              <p className="text-sm text-gray-700">{t('admin:backoffice.calls.loadError')}</p>
              {callsLoad.code && <p className="font-mono text-xs text-gray-500">{callsLoad.code}</p>}
            </div>
            {callsLoad.onRetry && (
              <Button variant="outline" size="sm" className="min-h-11" onClick={callsLoad.onRetry}>{t('common:actions.retry')}</Button>
            )}
          </div>
        ) : (
          <CallsTable
            calls={lastCalls}
            onPlayRecording={(url) => (onPlayRecording ? onPlayRecording(url) : window.open(url, '_blank', 'noopener'))}
          />
        )}
      </section>
    </div>
  );
}
